import { useMemo, useState } from "react";

const LS_ARBITRE_NOM = "yoma_arbitre_nom";
const LS_ARBITRE_VILLE = "yoma_arbitre_ville";
const LS_ARBITRE_LANGUE = "yoma_arbitre_langue";
const LS_ARBITRE_HONORAIRES = "yoma_arbitre_honoraires";

function readArbitreLs(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v != null && v !== "" ? v : fallback;
  } catch {
    return fallback;
  }
}
import { supabase } from "../supabaseClient";

const TYPE_MISSION_OPTIONS = [
  { value: "architecture", label: "Architecture Intérieure" },
  { value: "decoration", label: "Décoration & FF&E" },
  { value: "opc", label: "OPC — Coordination & Pilotage" },
  { value: "cle_en_main", label: "Clé en main" },
  { value: "mandat", label: "Mandat Financier" },
  { value: "promotion", label: "Promotion Immobilière" },
];

const CONTRAT_TITRES_PAR_MISSION = {
  architecture: "CONTRAT DE MISSION D'ARCHITECTURE INTÉRIEURE",
  decoration: "CONTRAT DE MISSION DÉCORATION & FF&E",
  opc: "CONTRAT D'ORDONNANCEMENT, PILOTAGE ET COORDINATION (OPC)",
  cle_en_main: "CONTRAT DE COORDINATION GLOBALE & RÉALISATION CLÉ EN MAIN",
  mandat: "CONTRAT DE MANDAT DE GESTION FINANCIÈRE DU PROJET",
  promotion: "CONTRAT DE COORDINATION DE PROJET IMMOBILIER & MANDAT",
};

const PROTECTION_CLAUSES = {
  architecture: `YOMA DESIGN n'assume aucune responsabilité quant à 
l'exécution des travaux par les entreprises. 
Toute validation de plan engage la responsabilité 
du client Maître d'Ouvrage.`,
  decoration: `YOMA DESIGN ne garantit pas les délais de livraison 
fournisseurs tiers. La qualité des produits livrés 
relève de la responsabilité exclusive du fournisseur.`,
  opc: `YOMA DESIGN agit exclusivement en qualité de 
coordinateur. Elle n'assume aucune responsabilité 
technique sur l'exécution des travaux ni sur les 
malfaçons des entreprises d'exécution.`,
  cle_en_main: `YOMA DESIGN intervient exclusivement en qualité de 
mandataire et coordinateur. Elle n'est pas entrepreneur 
général et n'assume aucune responsabilité d'exécution 
technique.`,
  mandat: `Les fonds reçus par YOMA DESIGN sont détenus au nom 
et pour le compte exclusif du Maître d'Ouvrage. 
Ils ne deviennent jamais propriété de YOMA DESIGN 
et sont affectés exclusivement au projet désigné. 
Toute affectation à un autre projet est expressément 
interdite.`,
  promotion: `YOMA DESIGN intervient exclusivement en qualité 
de coordinateur et mandataire du Maître d'Ouvrage 
Promoteur. Elle n'est pas promoteur immobilier, 
n'assume aucune responsabilité de promotion, 
de commercialisation, ni de garantie financière 
d'achèvement. Les fonds reçus sont détenus 
exclusivement au nom et pour le compte du 
Maître d'Ouvrage Promoteur.`,
};

const MISSIONS_DISPONIBLES = [
  { id: "coordination",    label: "La coordination générale du projet immobilier" },
  { id: "opc",             label: "L'Ordonnancement, le Pilotage et la Coordination (OPC)" },
  { id: "assistance_adm",  label: "L'assistance administrative du Maître d'Ouvrage" },
  { id: "gestion_fin",     label: "La gestion financière des flux travaux dans le cadre d'un mandat exprès" },
  { id: "suivi_chantier",  label: "Le suivi et le contrôle de l'avancement des travaux" },
  { id: "reception",       label: "L'assistance à la réception des travaux et levée des réserves" },
  { id: "reporting",       label: "Le reporting régulier au Maître d'Ouvrage" },
  { id: "selection_entre", label: "La sélection et référencement des entreprises d'exécution" },
];

function getYousignConfig() {
  try {
    const yousignKey = localStorage.getItem("yoma_yousign_api_key");
    const yousignEnv = localStorage.getItem("yoma_yousign_env") || "test";
    return { yousignKey, yousignEnv };
  } catch {
    return { yousignKey: null, yousignEnv: "test" };
  }
}

export default function ContractGeneratorPage({ projectData = {}, setProjectData, cabinetId = null }) {
  const [missionType, setMissionType] = useState("");
  const [showSigModal, setShowSigModal] = useState(false);
  const [signataireEmail, setSignataireEmail] = useState("");
  const [signataireNom, setSignataireNom] = useState("");
  const [sigSending, setSigSending] = useState(false);
  const [sigSuccess, setSigSuccess] = useState("");
  const [sigError, setSigError] = useState("");

  const [arbitreNom, setArbitreNom] = useState(() => readArbitreLs(LS_ARBITRE_NOM, ""));
  const [arbitreVille, setArbitreVille] = useState(() => readArbitreLs(LS_ARBITRE_VILLE, "Marrakech"));
  const [arbitreLangue, setArbitreLangue] = useState(() => {
    const v = readArbitreLs(LS_ARBITRE_LANGUE, "Français");
    return ["Français", "Arabe", "Anglais"].includes(v) ? v : "Français";
  });
  const [arbitreHonoraires, setArbitreHonoraires] = useState(() => readArbitreLs(LS_ARBITRE_HONORAIRES, "3.6"));

  const {
    nomClient, nomProjet, adresseProjet, dateContrat,
    montantGlobal, jalonsPaiement,
    planningPrevisionnel,
    includeAnnexeA, includeAnnexeB, includeAnnexeC,
    includeClauseArbitrage, includeClauseBlocage, includeClauseSubstitution,
  } = projectData;
  const perimetreMission = projectData.perimetreMission || {};

  function updateField(field, value) {
    if (!setProjectData) return;
    setProjectData((prev) => ({ ...prev, [field]: value }));
  }

  const MISSION_KEYS = {
    coordination: ["analyseFaisabilite", "verificationFonciere", "coordinationArchitecte"],
    opc: ["visitesChantier", "comptesRendusVisite", "suiviPlanning", "validationJalons"],
    assistance_adm: ["contratExecutionTravaux"],
    gestion_fin: ["receptionFonds", "comptabiliteAnalytique", "versementsEntreprise", "blocagePaiements"],
    selection_entre: ["selectionEntreprise", "contratExecutionTravaux"],
    suivi_chantier: ["visitesChantier", "comptesRendusVisite", "suiviPlanning", "validationJalons"],
    reporting: ["comptesRendusVisite", "suiviPlanning", "validationJalons"],
    reception: ["receptionContradictoire", "procesVerbalReception", "leveeReserves", "remiseDossierCloture"],
  };

  function allMissionKeysUnion() {
    const s = new Set();
    MISSIONS_DISPONIBLES.forEach((m) => {
      (MISSION_KEYS[m.id] || []).forEach((k) => s.add(k));
    });
    return Array.from(s);
  }

  function applyTemplate(type) {
    setMissionType(type || "");
    if (!setProjectData || !type) return;

    const keysUnion = allMissionKeysUnion();

    const TEMPLATES = {
      architecture: {
        activeIds: ["coordination", "assistance_adm", "suivi_chantier", "reporting"],
        includeAnnexeA: false,
        includeAnnexeB: true,
        includeAnnexeC: false,
        includeClauseArbitrage: true,
        includeClauseBlocage: false,
        includeClauseSubstitution: false,
      },
      decoration: {
        activeIds: ["selection_entre", "reporting"],
        includeAnnexeA: false,
        includeAnnexeB: true,
        includeAnnexeC: false,
        includeClauseArbitrage: true,
        includeClauseBlocage: false,
        includeClauseSubstitution: false,
      },
      opc: {
        activeIds: ["opc", "suivi_chantier", "reporting", "coordination"],
        includeAnnexeA: false,
        includeAnnexeB: true,
        includeAnnexeC: true,
        includeClauseArbitrage: true,
        includeClauseBlocage: true,
        includeClauseSubstitution: false,
      },
      cle_en_main: {
        activeIds: MISSIONS_DISPONIBLES.map((m) => m.id),
        includeAnnexeA: true,
        includeAnnexeB: true,
        includeAnnexeC: true,
        includeClauseArbitrage: true,
        includeClauseBlocage: true,
        includeClauseSubstitution: true,
      },
      mandat: {
        activeIds: ["gestion_fin", "reception", "reporting", "assistance_adm"],
        includeAnnexeA: true,
        includeAnnexeB: true,
        includeAnnexeC: false,
        includeClauseArbitrage: true,
        includeClauseBlocage: true,
        includeClauseSubstitution: true,
      },
      promotion: {
        activeIds: [
          "coordination",
          "gestion_fin",
          "assistance_adm",
          "selection_entre",
          "reporting",
          "reception",
        ],
        includeAnnexeA: true,
        includeAnnexeB: true,
        includeAnnexeC: true,
        includeClauseArbitrage: true,
        includeClauseBlocage: true,
        includeClauseSubstitution: true,
      },
    };

    const cfg = TEMPLATES[type];
    if (!cfg) return;

    const nextPerim = { ...perimetreMission };
    keysUnion.forEach((k) => { nextPerim[k] = false; });
    cfg.activeIds.forEach((mid) => {
      (MISSION_KEYS[mid] || []).forEach((k) => { nextPerim[k] = true; });
    });

    updateField("perimetreMission", nextPerim);
    updateField("includeAnnexeA", cfg.includeAnnexeA);
    updateField("includeAnnexeB", cfg.includeAnnexeB);
    updateField("includeAnnexeC", cfg.includeAnnexeC);
    updateField("includeClauseArbitrage", cfg.includeClauseArbitrage);
    updateField("includeClauseBlocage", cfg.includeClauseBlocage);
    updateField("includeClauseSubstitution", cfg.includeClauseSubstitution);
  }

  function isMissionChecked(missionId) {
    const keys = MISSION_KEYS[missionId] || [];
    return keys.some((k) => !!perimetreMission[k]);
  }

  const missionsActives = MISSIONS_DISPONIBLES.filter((m) => isMissionChecked(m.id));

  function handleToggleMission(missionId, nextChecked) {
    const keys = MISSION_KEYS[missionId] || [];
    const currentPerim = projectData.perimetreMission || {};

    if (!setProjectData) return;

    if (nextChecked) {
      const nextPerim = { ...currentPerim };
      keys.forEach((k) => { nextPerim[k] = true; });
      setProjectData((prev) => ({ ...prev, perimetreMission: nextPerim }));
      return;
    }

    const activeNow = {};
    MISSIONS_DISPONIBLES.forEach((m) => { activeNow[m.id] = isMissionChecked(m.id); });
    delete activeNow[missionId];

    const nextPerim = { ...currentPerim };
    keys.forEach((k) => {
      const coveredByOtherActive = Object.keys(activeNow).some((otherId) => {
        const otherKeys = MISSION_KEYS[otherId] || [];
        return otherKeys.includes(k);
      });
      if (!coveredByOtherActive) nextPerim[k] = false;
    });
    setProjectData((prev) => ({ ...prev, perimetreMission: nextPerim }));
  }

  const emetteurNom = projectData.emetteur_nom || "YOMA DESIGN";
  const emetteurRepresentant = projectData.emetteur_representant || "Monsieur Youssef KABDANI";
  const emetteurDesignation = projectData.emetteur_designation || emetteurNom;

  const GOLD     = "#C9A96E";
  const INK      = "#1C1915";
  const INK_SOFT = "#4A4540";
  const BORDER   = "#E7E1D8";
  const SOFT_BG  = "#F7F5F2";
  const WHITE    = "#FFFFFF";

  const cardStyle = {
    border: `1px solid ${BORDER}`, borderRadius: 12,
    backgroundColor: WHITE, padding: "22px 24px", marginBottom: 16,
  };
  const sectionTitle = {
    fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase",
    color: GOLD, marginBottom: 14, fontWeight: 700,
  };
  const buttonStyle = {
    padding: "12px 16px", borderRadius: 10, border: "none",
    backgroundColor: "#111", color: WHITE, cursor: "pointer", fontWeight: 700,
  };
  const secondaryButtonStyle = {
    ...buttonStyle, backgroundColor: GOLD, color: INK,
  };

  function formatMoney(value) {
    return Number(value || 0).toLocaleString("fr-FR") + " MAD";
  }
  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  const pieces = [
    "Contrat principal",
    includeAnnexeA ? "Annexe A — Échéancier de paiement" : null,
    includeAnnexeB ? "Annexe B — Périmètre de mission" : null,
    includeAnnexeC ? "Annexe C — Planning prévisionnel" : null,
    includeClauseArbitrage ? "Clause d'arbitrage" : null,
    includeClauseBlocage ? "Clause de blocage de paiement" : null,
    includeClauseSubstitution ? "Clause de substitution constructeur" : null,
  ].filter(Boolean);

  const article1Missions = missionsActives.length > 0
    ? missionsActives.map(m => `— ${m.label}`).join(",\n")
    : "— [Aucune mission sélectionnée]";

  const contractMainTitle =
    CONTRAT_TITRES_PAR_MISSION[missionType] || "CONTRAT DE COORDINATION GLOBALE & MANDAT";

  const contratPrincipal = useMemo(() => {
    const protectionText =
      missionType && PROTECTION_CLAUSES[missionType]
        ? PROTECTION_CLAUSES[missionType].replace(/YOMA DESIGN/g, emetteurNom)
        : "";

    return `
${emetteurNom}
${contractMainTitle}

PROJET : ${nomProjet || "[NOM DU PROJET]"}

ENTRE LES SOUSSIGNÉS

${nomClient || "[NOM DU CLIENT]"}
Ci-après désigné(e) : « Le Maître d'Ouvrage »

ET

${emetteurNom}
Représentée par ${emetteurRepresentant}
Ci-après désignée : « ${emetteurDesignation} »

ARTICLE 1 — OBJET DU CONTRAT
Le présent contrat a pour objet :
${article1Missions}.

${emetteurNom} n'exécute aucun travail matériel.
${emetteurNom} n'assume aucune mission de maîtrise d'œuvre technique.

ARTICLE 2 — QUALIFICATION JURIDIQUE
${emetteurNom} agit exclusivement en qualité de :
— Mandataire du Maître d'Ouvrage,
— Coordinateur OPC.

${emetteurNom} :
— N'est pas entrepreneur général,
— N'est pas constructeur,
— N'assume aucune responsabilité technique d'exécution,
— N'est tenue qu'à une obligation de moyens renforcée dans le cadre de sa mission de coordination.

ARTICLE 3 — MANDAT EXPRÈS DE GESTION FINANCIÈRE
Le Maître d'Ouvrage confère expressément à ${emetteurNom}, qui l'accepte, un mandat civil limité au projet désigné.
À ce titre :
— ${emetteurNom} est habilitée à recevoir les fonds destinés au projet,
— Les sommes destinées au règlement des entreprises sont détenues au nom et pour le compte du Maître d'Ouvrage,
— Ces fonds ne deviennent jamais la propriété de ${emetteurNom},
— Ils sont affectés exclusivement au projet concerné,
— Toute affectation à un autre projet est expressément interdite.

ARTICLE 4 — SÉPARATION DES ENVELOPPES
Le montant global convenu est structuré en deux enveloppes distinctes.
Cette structuration relève de l'architecture contractuelle interne de ${emetteurNom}.
Le client ne se voit présenter que le montant global convenu.

ARTICLE 5 — MONTANT GLOBAL ET ÉCHÉANCIER
Le montant global convenu pour la mission relative au projet ${nomProjet || "[NOM DU PROJET]"} est fixé à :
${formatMoney(montantGlobal)}

Les modalités de paiement sont détaillées en Annexe A.
Chaque versement est conditionné à la validation du jalon correspondant par ${emetteurNom}.

ARTICLE 6 — RESPONSABILITÉ DES ENTREPRISES
L'entreprise d'exécution demeure seule responsable :
— De l'exécution technique des travaux,
— De la conformité réglementaire,
— Des garanties légales applicables,
— De la sécurité du chantier,
— De ses salariés et sous-traitants.

ARTICLE 7 — SUSPENSION DES PAIEMENTS
${includeClauseBlocage ? "Voir clause de blocage de paiement." : ""}

ARTICLE 8 — DÉFAILLANCE ET SUBSTITUTION
${includeClauseSubstitution ? "Voir clause de substitution constructeur." : ""}

ARTICLE 9 — MODIFICATIONS — AVENANTS
Toute modification technique, budgétaire ou de périmètre doit faire l'objet d'un avenant écrit signé par les deux parties.
Aucune instruction orale ne modifie le présent contrat.

ARTICLE 10 — COMMUNICATION
Toute communication technique ou financière engageant le projet doit transiter exclusivement par ${emetteurNom}.

ARTICLE 11 — LIMITATION DE RESPONSABILITÉ
${emetteurNom} ne pourra être tenue responsable :
— Des malfaçons techniques imputables aux entreprises,
— Des retards imputables aux entreprises ou aux administrations,
— Des décisions administratives indépendantes de sa volonté,
— Des cas de force majeure,
— Des modifications décidées sans avenant.

ARTICLE 12 — CONFIDENTIALITÉ
Les conditions financières convenues entre ${emetteurNom} et les entreprises partenaires relèvent de relations contractuelles distinctes et confidentielles.

ARTICLE 13 — RÉSILIATION
Toute résiliation doit être précédée d'une mise en demeure écrite laissant un délai de 10 jours ouvrés pour régularisation.

ARTICLE 14 — RÈGLEMENT DES LITIGES
${includeClauseArbitrage ? "Voir clause d'arbitrage." : ""}

ARTICLE 15 — DROIT APPLICABLE
Le présent contrat est soumis au droit marocain.

ARTICLE 16 — ANNEXES
Le présent contrat est complété par les annexes suivantes, qui en font partie intégrante :
— Annexe A — Échéancier de paiement
— Annexe B — Périmètre de mission
— Annexe C — Planning prévisionnel du projet
${protectionText ? `

ARTICLE 17 — DISPOSITIONS PARTICULIÈRES DE PROTECTION
${protectionText}
` : ""}

SIGNATURES
Fait le : ${dateContrat || "[DATE]"}
Le Maître d'Ouvrage
${emetteurNom}
    `.trim();
  }, [
    nomClient,
    nomProjet,
    adresseProjet,
    montantGlobal,
    dateContrat,
    article1Missions,
    contractMainTitle,
    missionType,
    emetteurNom,
    includeClauseArbitrage,
    includeClauseBlocage,
    includeClauseSubstitution,
  ]);

  const annexeA = useMemo(() => {
    return `
ANNEXE A — ÉCHÉANCIER DE PAIEMENT

Projet : ${nomProjet || "[NOM DU PROJET]"}
Contrat signé le : ${dateContrat || "[DATE]"}
Montant global : ${formatMoney(montantGlobal)}

STRUCTURE DES VERSEMENTS
VERSEMENT 1 — 5 % du montant global
Intitulé : Provision d'activation
Condition de déclenchement : Signature du présent contrat.

VERSEMENT 2 — 30 % du montant global
Intitulé : Démarrage effectif des travaux
Condition de déclenchement : Constat de démarrage physique du chantier validé par ${emetteurNom}.

VERSEMENT 3 — 20 % du montant global
Intitulé : Fondations & première dalle
Condition de déclenchement : Validation écrite de ${emetteurNom} émise.

VERSEMENT 4 — 20 % du montant global
Intitulé : Achèvement du gros œuvre
Condition de déclenchement : Validation écrite de ${emetteurNom} émise.

VERSEMENT 5 — 20 % du montant global
Intitulé : Second œuvre
Condition de déclenchement : Validation écrite de ${emetteurNom} émise.

VERSEMENT 6 — 5 % du montant global
Intitulé : Livraison & réception
Condition de déclenchement : Procès-verbal de réception contradictoire signé et réserves levées.

RÈGLES APPLICABLES
${jalonsPaiement || "[JALONS DE PAIEMENT À COMPLÉTER]"}
    `.trim();
  }, [nomProjet, dateContrat, montantGlobal, jalonsPaiement]);

  const annexeB = useMemo(() => {
    return `
ANNEXE B — PÉRIMÈTRE DE MISSION

Projet : ${nomProjet || "[NOM DU PROJET]"}
Contrat signé le : ${dateContrat || "[DATE]"}

SECTION 1 — CE QUE ${emetteurNom} FAIT

PHASE 1 — STRUCTURATION ADMINISTRATIVE
— Analyse de faisabilité et cadrage budgétaire initial
— Vérification de la situation foncière et administrative
— Coordination avec l'architecte pour validation des plans
— Sélection et référencement de l'entreprise d'exécution
— Rédaction et signature du Contrat d'Exécution des Travaux
— Ouverture du dossier projet
— Transmission du dossier de démarrage au Maître d'Ouvrage

PHASE 2 — PILOTAGE DU CHANTIER
— Visites de chantier régulières
— Comptes rendus de visite formalisés
— Suivi du planning et alerte en cas de dérive
— Validation des jalons
— Gestion des demandes de modification
— Interface unique entre le Maître d'Ouvrage et l'entreprise
— Reporting client structuré

PHASE 3 — GESTION FINANCIÈRE
— Réception des fonds du Maître d'Ouvrage
— Tenue de la comptabilité analytique du projet
— Versements à l'entreprise sur validation de jalons
— État des fonds disponibles sur demande
— Blocage des paiements en cas de non-conformité
— Restitution des fonds non engagés en cas de résiliation

PHASE 4 — RÉCEPTION ET CLÔTURE
— Organisation de la réception contradictoire
— Rédaction du procès-verbal de réception
— Suivi de la levée des réserves
— Versement du solde après levée complète
— Clôture documentaire et archivage du dossier

SECTION 2 — CE QUE ${emetteurNom} NE FAIT PAS
— Pas de conception architecturale
— Pas de contrôle technique réglementaire
— Pas de maîtrise d'œuvre d'exécution
— Pas de responsabilité des choix techniques de l'entreprise
— Pas de gestion des litiges entre entreprises et sous-traitants
— Pas de démarches administratives communales sauf mention expresse

SECTION 3 — DESCRIPTION DU PROJET
Adresse / Référence cadastrale : ${adresseProjet || "[ADRESSE / RÉFÉRENCE CADASTRALE]"}
Durée prévisionnelle du chantier : ${planningPrevisionnel || "[DURÉE À COMPLÉTER]"}
    `.trim();
  }, [nomProjet, dateContrat, adresseProjet, planningPrevisionnel]);

  const annexeC = useMemo(() => {
    return `
ANNEXE C — PLANNING PRÉVISIONNEL DU PROJET

Projet : ${nomProjet || "[NOM DU PROJET]"}
Client : ${nomClient || "[NOM DU CLIENT]"}
Adresse du projet : ${adresseProjet || "[ADRESSE / TERRAIN]"}
Date de signature du contrat : ${dateContrat || "[DATE]"}

1 — OBJET DU DOCUMENT
Le présent document définit le planning prévisionnel des principales étapes du projet, à titre indicatif, dans le cadre de la mission de coordination confiée à ${emetteurNom}.

2 — PLANNING PRÉVISIONNEL
${planningPrevisionnel || `Signature du contrat\nStructuration du projet\nObtention du permis de construire\nDémarrage des travaux\nFondations\nGros œuvre\nSecond œuvre\nFinitions\nRéception des travaux`}

3 — NATURE INDICATIVE DU PLANNING
Le présent planning est prévisionnel et peut faire l'objet d'ajustements en fonction :
— des délais administratifs
— des conditions techniques du chantier
— des conditions climatiques
— des délais d'approvisionnement des matériaux
— ou de tout événement indépendant de la volonté de ${emetteurNom}.
    `.trim();
  }, [nomProjet, nomClient, adresseProjet, dateContrat, planningPrevisionnel]);

  const clauseArbitrage = useMemo(() => {
    const nomAff = arbitreNom.trim() || "[NOM ARBITRE]";
    const villeAff = arbitreVille.trim() || "[VILLE]";
    const langMap = {
      Français: "le français",
      Arabe: "l'arabe",
      Anglais: "l'anglais",
    };
    const langAff = arbitreLangue && langMap[arbitreLangue] ? langMap[arbitreLangue] : "[LANGUE]";
    const hStr = String(arbitreHonoraires ?? "").trim();
    const hAff = hStr === "" ? "[HONORAIRES]" : hStr.replace(/\./g, ",");

    return `
CLAUSE D'ARBITRAGE

14.1 — TENTATIVE DE RÉSOLUTION AMIABLE PRÉALABLE
Tout différend relatif à l'interprétation, l'exécution ou la résiliation du présent contrat fera l'objet d'une tentative obligatoire de résolution amiable.

14.2 — CLAUSE COMPROMISSOIRE
À défaut de résolution amiable, tout différend sera définitivement tranché par voie d'arbitrage conformément à la loi marocaine n° 95-17.

14.3 — ARBITRE UNIQUE
Les Parties désignent d'un commun accord ${nomAff} en qualité d'arbitre unique.

14.4 — MODALITÉS DE L'ARBITRAGE
La langue de l'arbitrage est ${langAff}.
Le siège de l'arbitrage est fixé à ${villeAff}.

14.5 — FRAIS D'ARBITRAGE
Les frais de l'arbitrage et les honoraires de l'arbitre sont fixés à ${hAff}% du montant du litige.

14.6 — MESURES CONSERVATOIRES
La clause compromissoire n'empêche pas la saisine du juge des référés du Tribunal de Commerce de Marrakech pour mesures conservatoires urgentes.

14.7 — RECOURS EN ANNULATION
La juridiction saisie sera le Tribunal de Commerce de Marrakech.
  `.trim();
  }, [arbitreNom, arbitreVille, arbitreLangue, arbitreHonoraires]);

  const clauseBlocage = `
CLAUSE DE BLOCAGE DE PAIEMENT

ARTICLE 7 — SUSPENSION DES PAIEMENTS AUX ENTREPRISES

7.1 — DROIT DE BLOCAGE
${emetteurNom} dispose du droit de suspendre tout versement à l'entreprise d'exécution dans l'intérêt du Maître d'Ouvrage.

7.2 — CAS DE DÉCLENCHEMENT
— Non-conformité constatée et documentée
— Défaut grave objectivable
— Abandon partiel ou total du chantier
— Manquement contractuel grave
— Absence des assurances ou documents requis

7.3 — PROCÉDURE
Toute suspension est notifiée par écrit, proportionnée, accompagnée d'un délai de régularisation.

7.4 — LEVÉE
La suspension cesse après régularisation documentée et validation écrite de ${emetteurNom}.

7.5 — LIMITE DE RESPONSABILITÉ
${emetteurNom} ne peut être tenue responsable des conséquences d'une suspension exercée de bonne foi.
  `.trim();

  const clauseSubstitution = `
CLAUSE DE SUBSTITUTION CONSTRUCTEUR

ARTICLE 8 — DÉFAILLANCE ET SUBSTITUTION DE L'ENTREPRISE

8.1 — DÉFINITION DE LA DÉFAILLANCE
— Abandon de chantier
— Retard grave injustifié de plus de 30 jours
— Non-conformité structurelle ou technique majeure
— Insolvabilité avérée ou dépôt de bilan

8.2 — PROCÉDURE DE SUBSTITUTION
Étape 1 — Constat documenté
Étape 2 — Mise en demeure
Étape 3 — Résiliation
Étape 4 — Inventaire et état financier
Étape 5 — Sélection du nouveau prestataire
Étape 6 — Signature d'un avenant

8.3 — CHARGES FINANCIÈRES DE LA SUBSTITUTION
Tout différentiel de coût résultant de la substitution demeure à la charge du Maître d'Ouvrage.

8.4 — CONTINUITÉ DU PROJET
${emetteurNom} assure la continuité de sa mission de coordination pendant la procédure de substitution.
  `.trim();

  const dossierTexte = useMemo(() => {
    const sections = [contratPrincipal];
    if (includeAnnexeA) sections.push(annexeA);
    if (includeAnnexeB) sections.push(annexeB);
    if (includeAnnexeC) sections.push(annexeC);
    if (includeClauseArbitrage) sections.push(clauseArbitrage);
    if (includeClauseBlocage) sections.push(clauseBlocage);
    if (includeClauseSubstitution) sections.push(clauseSubstitution);
    return sections.join("\n\n----------------------------------------\n\n");
  }, [
    contratPrincipal,
    annexeA,
    annexeB,
    annexeC,
    clauseArbitrage,
    clauseBlocage,
    clauseSubstitution,
    includeAnnexeA,
    includeAnnexeB,
    includeAnnexeC,
    includeClauseArbitrage,
    includeClauseBlocage,
    includeClauseSubstitution,
  ]);

  function enregistrerDefautsArbitre() {
    try {
      localStorage.setItem(LS_ARBITRE_NOM, arbitreNom);
      localStorage.setItem(LS_ARBITRE_VILLE, arbitreVille);
      localStorage.setItem(LS_ARBITRE_LANGUE, arbitreLangue);
      localStorage.setItem(LS_ARBITRE_HONORAIRES, String(arbitreHonoraires));
      alert("Paramètres de l'arbitre enregistrés comme défaut.");
    } catch {
      alert("Enregistrement impossible.");
    }
  }

  function buildPrintableHtml() {
    return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"/>
<title>Dossier contractuel - ${escapeHtml(nomProjet || "Projet")}</title>
<style>body{font-family:Arial,sans-serif;color:#111;margin:35px;line-height:1.55;}h1,h2{margin-bottom:8px;}.title{text-align:center;margin-bottom:25px;}.box{border:1px solid #ccc;border-radius:10px;padding:16px;margin-bottom:20px;}pre{white-space:pre-wrap;font-family:Arial,sans-serif;margin:0;}ul{margin-top:8px;}@media print{body{margin:18mm;}}</style>
</head><body>
<div class="title"><h1>${escapeHtml(emetteurNom)}</h1><h2>Dossier contractuel complet</h2><div>${escapeHtml(nomProjet || "Projet non renseigné")}</div></div>
<div class="box"><h3>Pièces retenues</h3><ul>${pieces.map(p => `<li>${escapeHtml(p)}</li>`).join("")}</ul></div>
<div class="box"><pre>${escapeHtml(dossierTexte)}</pre></div>
</body></html>`;
  }

  function handlePrint() {
    const w = window.open("", "_blank", "width=1000,height=800");
    if (!w) return;
    w.document.open();
    w.document.write(buildPrintableHtml());
    w.document.close();
    w.onload = () => { w.focus(); w.print(); };
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(dossierTexte);
      alert("Dossier contractuel copié.");
    } catch { alert("Copie impossible."); }
  }

  const contratStatutSignature = projectData.contratStatutSignature || "non_signe";
  const signatureBadge = (() => {
    if (projectData.contratSigne === true || contratStatutSignature === "signe") {
      return {
        label: "Signé",
        style: { background: "rgba(26,122,68,0.12)", color: "#1A7A44", border: "1px solid rgba(26,122,68,0.4)" },
      };
    }
    if (contratStatutSignature === "en_attente") {
      return {
        label: "En attente",
        style: { background: "rgba(214,137,16,0.14)", color: "#D68910", border: "1px solid rgba(214,137,16,0.45)" },
      };
    }
    return {
      label: "Non signé",
      style: { background: "rgba(0,0,0,0.06)", color: INK_SOFT, border: `1px solid ${BORDER}` },
    };
  })();

  const { yousignKey } = getYousignConfig();
  const hasYousignKey = Boolean(yousignKey?.trim());

  async function handleSignature(emailSignataire, nomSignataire) {
    const yousignKey = localStorage.getItem("yoma_yousign_api_key");
    const yousignEnv = localStorage.getItem("yoma_yousign_env") || "sandbox";

    if (!yousignKey) {
      alert("Configurez votre clé Yousign dans OUTILS → Branding / Intégrations");
      return false;
    }

    setSigSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("yousign-send", {
        body: {
          signataireName: nomSignataire,
          signaireEmail: emailSignataire,
          contratTexte: dossierTexte,
          nomProjet: nomProjet || "Projet YOMA",
          yousignApiKey: yousignKey,
          yousignEnv: yousignEnv,
        },
      });

      if (error || !data?.success) {
        alert("Erreur envoi signature : " + (data?.error || error?.message));
        return false;
      }

      if (setProjectData) {
        setProjectData((prev) => ({
          ...prev,
          contratStatutSignature: "en_attente",
          contratSignatureEnAttenteLabel: "En attente de signature",
        }));
      }
      alert(`Invitation de signature envoyée à ${emailSignataire}`);
      return true;
    } catch (err) {
      alert("Erreur : " + err.message);
      return false;
    } finally {
      setSigSending(false);
    }
  }

  async function handleEnvoyerSignatureSubmit(e) {
    e?.preventDefault?.();
    setSigError("");
    setSigSuccess("");
    const email = signataireEmail.trim();
    const nom = signataireNom.trim();
    if (!email) {
      setSigError("Indiquez l’email du signataire.");
      return;
    }

    try {
      const ok = await handleSignature(email, nom);
      if (!ok) return;
      setSigSuccess(`Invitation de signature envoyée à ${email}`);
      setShowSigModal(false);
      setSignataireEmail("");
      setSignataireNom("");
    } catch (err) {
      setSigError(err?.message || String(err) || "Envoi impossible.");
    }
  }

  const goldButtonStyle = {
    padding: "12px 16px",
    borderRadius: 10,
    border: "none",
    backgroundColor: GOLD,
    color: INK,
    cursor: "pointer",
    fontWeight: 700,
  };
  const disabledGoldStyle = {
    ...goldButtonStyle,
    backgroundColor: "#ccc",
    color: "#666",
    cursor: "not-allowed",
  };

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: INK_SOFT }}>Chargement...</div>;
  }

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 1100 }}><div style={{ marginBottom: 24 }}><div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, marginBottom: 4 }}>Contractualisation</div><div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}><div style={{ fontSize: 24, fontWeight: 700 }}>Générateur de Contrat</div><span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                padding: "6px 12px",
                borderRadius: 999,
                ...signatureBadge.style,
              }}
            >
              {signatureBadge.label}
            </span></div><div style={{ fontSize: 13, color: INK_SOFT, marginTop: 3 }}>Dossier contractuel complet assemblé à partir des données projet</div><div style={{ fontSize: 12, color: INK_SOFT, marginTop: 6, fontStyle: "italic" }}>Les annexes et clauses incluses dépendent des réglages du projet.</div></div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}><div><div style={cardStyle}><div style={sectionTitle}>Données récupérées</div>
            {[
              { label: "Client",          val: nomClient },
              { label: "Projet",          val: nomProjet },
              { label: "Adresse",         val: adresseProjet },
              { label: "Date",            val: dateContrat },
              { label: "Montant global",  val: formatMoney(montantGlobal) },
            ].map(r => (
              <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${BORDER}`, fontSize: 13 }}><span style={{ color: INK_SOFT }}>{r.label}</span><span style={{ fontWeight: 600 }}>{r.val || "—"}</span></div>
            ))}
          </div><div style={cardStyle}><div style={sectionTitle}>Type de mission</div><select
              value={missionType}
              onChange={(e) => applyTemplate(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${BORDER}`,
                fontSize: 13,
                background: WHITE,
                color: INK,
              }}
            ><option value="">—</option>
              {TYPE_MISSION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select></div>

          {/* ── Sélection des missions Article 1 ── */}
          <div style={cardStyle}><div style={sectionTitle}>Article 1 — Missions du contrat</div><div style={{ fontSize: 12, color: INK_SOFT, marginBottom: 12 }}>
              Sélectionnez les missions à inclure dans l'objet du contrat :
            </div><div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {MISSIONS_DISPONIBLES.map(m => {
                const checked = isMissionChecked(m.id);
                return (
                  <label key={m.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 14px", borderRadius: 8, border: `1px solid ${checked ? GOLD : BORDER}`, background: checked ? GOLD + "12" : SOFT_BG, cursor: "pointer", transition: "all 0.15s" }}><input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => handleToggleMission(m.id, e.target.checked)}
                      style={{ accentColor: GOLD, width: 15, height: 15, marginTop: 1, flexShrink: 0 }}
                    /><span style={{ fontSize: 13, color: checked ? INK : INK_SOFT, fontWeight: checked ? 600 : 400, lineHeight: 1.4 }}>
                      {m.label}
                    </span></label>
                );
              })}
            </div>

            {missionsActives.length > 0 && (
              <div style={{ marginTop: 14, padding: "12px 14px", background: SOFT_BG, borderRadius: 8, borderLeft: `3px solid ${GOLD}` }}><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: GOLD, marginBottom: 8 }}>Aperçu Article 1</div><div style={{ fontSize: 12, color: INK, lineHeight: 1.7 }}>
                  Le présent contrat a pour objet :<br />
                  {missionsActives.map(m => <span key={m.id}>— {m.label}<br /></span>)}
                </div></div>
            )}
          </div><div style={cardStyle}><div style={sectionTitle}>Pièces retenues</div><ul style={{ margin: 0, paddingLeft: 18 }}>
              {pieces.map(p => <li key={p} style={{ fontSize: 13, marginBottom: 4 }}>{p}</li>)}
            </ul>
          </div>

          <div style={cardStyle}>
            <div style={sectionTitle}>Annexes &amp; Clauses</div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={!!includeClauseArbitrage}
                onChange={(e) => updateField("includeClauseArbitrage", e.target.checked)}
                style={{ accentColor: GOLD, width: 16, height: 16 }}
              />
              Inclure la clause d&apos;arbitrage
            </label>
            {includeClauseArbitrage ? (
              <div
                style={{
                  padding: "16px 18px",
                  borderRadius: 10,
                  border: `1px solid ${BORDER}`,
                  background: SOFT_BG,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, color: INK }}>Informations de l&apos;arbitre</div>
                <div style={{ display: "grid", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: INK_SOFT, marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      Nom de l&apos;arbitre
                    </label>
                    <input
                      type="text"
                      value={arbitreNom}
                      onChange={(e) => setArbitreNom(e.target.value)}
                      placeholder="ex. Monsieur X…"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: `1px solid ${BORDER}`,
                        fontSize: 13,
                        boxSizing: "border-box",
                        background: WHITE,
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: INK_SOFT, marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      Ville du siège
                    </label>
                    <input
                      type="text"
                      value={arbitreVille}
                      onChange={(e) => setArbitreVille(e.target.value)}
                      placeholder="ex. Marrakech"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: `1px solid ${BORDER}`,
                        fontSize: 13,
                        boxSizing: "border-box",
                        background: WHITE,
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: INK_SOFT, marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      Langue de l&apos;arbitrage
                    </label>
                    <select
                      value={arbitreLangue}
                      onChange={(e) => setArbitreLangue(e.target.value)}
                      style={{
                        width: "100%",
                        maxWidth: 280,
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: `1px solid ${BORDER}`,
                        fontSize: 13,
                        background: WHITE,
                        color: INK,
                      }}
                    >
                      <option value="Français">Français</option>
                      <option value="Arabe">Arabe</option>
                      <option value="Anglais">Anglais</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: INK_SOFT, marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      Honoraires arbitre (%)
                    </label>
                    <input
                      type="text"
                      value={arbitreHonoraires}
                      onChange={(e) => setArbitreHonoraires(e.target.value)}
                      placeholder="3.6"
                      style={{
                        width: "100%",
                        maxWidth: 160,
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: `1px solid ${BORDER}`,
                        fontSize: 13,
                        boxSizing: "border-box",
                        background: WHITE,
                      }}
                    />
                  </div>
                </div>
                <button type="button" onClick={enregistrerDefautsArbitre} style={{ ...secondaryButtonStyle, marginTop: 16 }}>
                  Enregistrer comme défaut
                </button>
                <p style={{ margin: "12px 0 0", fontSize: 11, color: INK_SOFT, lineHeight: 1.5 }}>
                  Ces valeurs sont injectées dans le texte de la clause (placeholders si un champ est vide).
                </p>
              </div>
            ) : null}
          </div>

          <div style={cardStyle}><div style={sectionTitle}>Actions</div><div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start", flexDirection: "column" }}><div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}><button type="button" style={buttonStyle} onClick={handlePrint}> Version imprimable</button><button type="button" style={secondaryButtonStyle} onClick={handleCopy}> Copier le dossier</button></div>
            {!hasYousignKey && (
              <p style={{ margin: "8px 0 0", fontSize: 12, color: INK_SOFT, maxWidth: 420, lineHeight: 1.5 }}>
                Configurez votre clé Yousign dans <strong>OUTILS → Branding / Intégrations</strong>
              </p>
            )}
            <button
              type="button"
              disabled={!hasYousignKey}
              style={hasYousignKey ? goldButtonStyle : disabledGoldStyle}
              onClick={() => {
                if (!getYousignConfig().yousignKey?.trim()) return;
                setSigError("");
                setSigSuccess("");
                setShowSigModal(true);
              }}
            >
              Envoyer pour signature
            </button>
            {sigSuccess ? (
              <div style={{ marginTop: 6, padding: "10px 12px", background: "rgba(26,122,68,0.1)", borderRadius: 8, fontSize: 13, color: "#1A7A44", fontWeight: 600 }}>
                {sigSuccess}
              </div>
            ) : null}
          </div></div></div><div style={cardStyle}><div style={sectionTitle}>Aperçu du dossier complet</div><pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0, lineHeight: 1.5, fontSize: 12, color: INK }}>
            {dossierTexte}
          </pre></div></div>

      {showSigModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={() => !sigSending && setShowSigModal(false)}
          role="presentation"
        >
          <div
            style={{
              background: WHITE,
              borderRadius: 14,
              padding: "28px 32px",
              maxWidth: 440,
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
              border: `1px solid ${BORDER}`,
            }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="sig-modal-title"
          >
            <h3 id="sig-modal-title" style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700 }}>
              Envoi pour signature
            </h3>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: INK_SOFT }}>
              Le client recevra un email l’invitant à signer le contrat (environnement Yousign : {getYousignConfig().yousignEnv}).
            </p>
            <form onSubmit={handleEnvoyerSignatureSubmit}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: INK_SOFT, marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Email du signataire
              </label>
              <input
                type="email"
                required
                value={signataireEmail}
                onChange={(e) => setSignataireEmail(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `1px solid ${BORDER}`,
                  fontSize: 14,
                  marginBottom: 14,
                  boxSizing: "border-box",
                }}
                placeholder="client@exemple.com"
              />
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: INK_SOFT, marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Nom du signataire
              </label>
              <input
                type="text"
                value={signataireNom}
                onChange={(e) => setSignataireNom(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `1px solid ${BORDER}`,
                  fontSize: 14,
                  marginBottom: 18,
                  boxSizing: "border-box",
                }}
                placeholder="Nom complet"
              />
              {sigError ? (
                <div style={{ marginBottom: 12, padding: "10px 12px", background: "#FDEDEC", borderRadius: 8, fontSize: 13, color: "#C0392B" }}>{sigError}</div>
              ) : null}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button
                  type="button"
                  disabled={sigSending}
                  onClick={() => setShowSigModal(false)}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 8,
                    border: `1px solid ${BORDER}`,
                    background: SOFT_BG,
                    cursor: sigSending ? "default" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  Annuler
                </button>
                <button type="submit" disabled={sigSending} style={sigSending ? { ...goldButtonStyle, opacity: 0.7, cursor: "wait" } : goldButtonStyle}>
                  {sigSending ? "Envoi…" : "Envoyer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
